import { BrandLogo, Button } from '@elva/ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { readApiError } from '../../api/errors';
import { getAppConfig } from '../../config/appConfigRef';
import { getLoginRedirectUri } from '../../config/getElvaLoginUrl';
import { validateOAuthClientRegistration } from '../sessionApi';
import { useAuthStore } from '../authStore';

const AUTO_REDIRECT_SECONDS = 5;

export interface AuthLoginGateProps {
  logoSrc?: string;
  autoRedirectSeconds?: number;
}

export function AuthLoginGate({
  logoSrc,
  autoRedirectSeconds = AUTO_REDIRECT_SECONDS,
}: AuthLoginGateProps) {
  const redirectToLogin = useAuthStore((s) => s.redirectToLogin);
  const config = getAppConfig();
  const appName = config.appName;
  const redirectUri = getLoginRedirectUri();
  const [secondsLeft, setSecondsLeft] = useState(autoRedirectSeconds);
  const [registrationError, setRegistrationError] = useState<string | null>(null);
  const [isCheckingRegistration, setIsCheckingRegistration] = useState(true);
  const timersRef = useRef<{
    timeout?: ReturnType<typeof setTimeout>;
    interval?: ReturnType<typeof setInterval>;
  }>({});

  const clearTimers = useCallback(() => {
    const { timeout, interval } = timersRef.current;
    if (timeout !== undefined) {
      clearTimeout(timeout);
      delete timersRef.current.timeout;
    }
    if (interval !== undefined) {
      clearInterval(interval);
      delete timersRef.current.interval;
    }
  }, []);

  const goToLogin = useCallback(() => {
    clearTimers();
    redirectToLogin();
  }, [clearTimers, redirectToLogin]);

  useEffect(() => {
    let cancelled = false;

    const checkRegistration = async () => {
      setIsCheckingRegistration(true);
      try {
        await validateOAuthClientRegistration(redirectUri);
        if (!cancelled) setRegistrationError(null);
      } catch (error) {
        const apiError = readApiError(error);
        if (!cancelled) {
          setRegistrationError(
            apiError?.code === 'invalid_client'
              ? `client_id "${config.clientId}" or redirect_uri "${redirectUri}" is not registered on ${config.accountApiUrl}. ` +
                  'The account app validates this with GET /auth/clients before login — both sides must use the same API, and Platform → Client apps must list this pair.'
              : 'Could not verify OAuth client registration. Check VITE_ACCOUNT_API_URL and try again.',
          );
        }
      } finally {
        if (!cancelled) setIsCheckingRegistration(false);
      }
    };

    void checkRegistration();

    return () => {
      cancelled = true;
    };
  }, [config.accountApiUrl, config.clientId, redirectUri]);

  useEffect(() => {
    if (isCheckingRegistration || registrationError) {
      return;
    }

    setSecondsLeft(autoRedirectSeconds);

    timersRef.current.timeout = setTimeout(() => {
      goToLogin();
    }, autoRedirectSeconds * 1000);

    timersRef.current.interval = setInterval(() => {
      setSecondsLeft((prev) => Math.max(0, prev - 1));
    }, 1000);

    return clearTimers;
  }, [autoRedirectSeconds, clearTimers, goToLogin, isCheckingRegistration, registrationError]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--cream-0)] px-4">
      <div className="w-full max-w-md space-y-6 text-center">
        <div className="flex justify-center">
          {logoSrc ? (
            <BrandLogo src={logoSrc} alt={appName} className="h-16 w-16" />
          ) : (
            <div className="h-16 w-16 rounded-2xl bg-[var(--grad-spectrum)]" />
          )}
        </div>
        <div className="space-y-2">
          <h1 className="font-[var(--font-display)] text-2xl font-semibold text-[var(--ink-0)]">
            Authentication required
          </h1>
          <p className="text-sm text-[var(--ink-2)]">
            Sign in with your Elva account to access {appName}.
          </p>
        </div>

        {registrationError ? (
          <div className="rounded-[var(--r-md)] border border-[var(--terracotta)]/30 bg-[var(--cream-1)] px-4 py-3 text-left text-xs text-[var(--ink-2)]">
            <p className="font-semibold text-[var(--ink-0)]">OAuth client not recognized</p>
            <p className="mt-2">{registrationError}</p>
            <dl className="mt-3 space-y-1 font-[var(--font-mono)] text-[10px] text-[var(--ink-3)]">
              <div>
                <dt className="inline">client_id: </dt>
                <dd className="inline">{config.clientId}</dd>
              </div>
              <div>
                <dt className="inline">redirect_uri: </dt>
                <dd className="inline break-all">{redirectUri}</dd>
              </div>
              <div>
                <dt className="inline">account UI: </dt>
                <dd className="inline break-all">{config.accountDomain}</dd>
              </div>
              <div>
                <dt className="inline">account API: </dt>
                <dd className="inline break-all">{config.accountApiUrl}</dd>
              </div>
            </dl>
          </div>
        ) : null}

        <div className="flex flex-col items-center gap-3">
          <Button
            type="button"
            variant="primary"
            size="lg"
            className="w-full max-w-[280px]"
            disabled={isCheckingRegistration}
            onClick={goToLogin}
          >
            {isCheckingRegistration ? 'Checking registration…' : 'Login with Elva Account'}
          </Button>
          {!registrationError && !isCheckingRegistration ? (
            <p className="text-xs text-[var(--ink-3)]">
              {secondsLeft > 0 ? (
                <>
                  Redirecting to the Elva account system in{' '}
                  <span className="font-semibold tabular-nums text-[var(--ink-2)]">
                    {secondsLeft}
                  </span>
                  {secondsLeft === 1 ? ' second' : ' seconds'}…
                </>
              ) : (
                'Redirecting to the Elva account system for authentication…'
              )}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
